import { CRLF, foldLine } from "../shared/line-fold.ts";
import { buildRRule } from "./rrule.ts";
import type { CalendarEvent } from "./types.ts";

/**
 * Graph event → iCalendar VEVENT (ARCHITECTURE.md §7/§8: `calendar/<calendar>.ics`).
 *
 * Pure and dependency-free, unlike export.ts — testable under plain Node.
 *
 * Every DTSTART/DTEND here is assumed already normalised to UTC by the
 * caller's request (`Prefer: outlook.timezone="UTC"`, see export.ts) — this
 * module never emits a VTIMEZONE block or a TZID parameter, deliberately:
 * Graph's default timezone identifiers are Windows zone names ("Pacific
 * Standard Time"), not the IANA names iCalendar expects, and mapping one to
 * the other correctly (with the right DST rules for the right years) is a
 * project of its own. Normalising to UTC up front sidesteps needing that
 * mapping at all, at the cost of every time in the exported file reading as
 * UTC rather than the calendar's original local zone.
 */

/** RFC 5545 §3.3.11 TEXT escaping — the same reserved-character set as vCard. */
function escapeText(value: string): string {
    return value.replace(/\\/g, "\\\\").replace(/,/g, "\\,").replace(/;/g, "\\;").replace(/\n/g, "\\n");
}

/** Minimal HTML→text for DESCRIPTION: block-level tags become newlines, everything else is stripped. */
function stripHtml(html: string): string {
    return html
        .replace(/<(br|\/p|\/div|\/li)\s*\/?>/gi, "\n")
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .trim();
}

function eventDescription(event: CalendarEvent): string | null {
    const content = event.body?.content;
    if (!content) {
        return null;
    }
    const text = event.body?.contentType === "html" ? stripHtml(content) : content.trim();
    return text || null;
}

/**
 * `2026-03-14T09:00:00.0000000` (or a proper `...Z`/offset variant) →
 * `20260314T090000Z`.
 *
 * Deliberately does not go through `new Date(iso)`: Graph, once asked for
 * `Prefer: outlook.timezone="UTC"`, returns start/end as a naked wall-clock
 * string with *no* `Z` or offset at all — it is UTC by virtue of the request,
 * not by anything in the string itself. Handing that to `Date()` would parse
 * it as local time in whatever timezone this code happens to run in, silently
 * shifting every event. Regex-extracting the components instead means the
 * result is correct regardless of the machine's own timezone, and a string
 * that does carry `Z`/an offset is handled the same way since the offset is
 * simply ignored — correct only because every caller here has already
 * requested UTC-normalised times from Graph in the first place.
 */
function formatDateTimeUtc(iso: string): string | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/.exec(iso);
    if (!match) {
        return null;
    }
    const [, y, mo, d, h, mi, s] = match;
    return `${y}${mo}${d}T${h}${mi}${s}Z`;
}

/** `2026-03-14T00:00:00` → `20260314`, for VALUE=DATE all-day boundaries. */
function formatDateOnly(iso: string): string | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return match ? `${match[1]}${match[2]}${match[3]}` : null;
}

function property(name: string, value: string | null): string | null {
    return value === null ? null : foldLine(`${name}${value}`);
}

/**
 * One VEVENT block. `forceStandalone` drops any RRULE even if the event
 * carries a recognisable recurrence pattern — used for exception/cancelled
 * occurrences, which are rendered as independent events rather than linked
 * via RECURRENCE-ID (see export.ts for why).
 */
export function renderEvent(event: CalendarEvent, opts: { forceStandalone?: boolean } = {}): string {
    const lines: string[] = ["BEGIN:VEVENT"];

    lines.push(`UID:${event.iCalUId || event.id}`);

    const stamp = formatDateTimeUtc(event.lastModifiedDateTime ?? new Date().toISOString());
    lines.push(`DTSTAMP:${stamp ?? formatDateTimeUtc(new Date().toISOString())}`);

    if (event.isAllDay) {
        const start = event.start?.dateTime ? formatDateOnly(event.start.dateTime) : null;
        const end = event.end?.dateTime ? formatDateOnly(event.end.dateTime) : null;
        if (start) {
            lines.push(`DTSTART;VALUE=DATE:${start}`);
        }
        if (end) {
            lines.push(`DTEND;VALUE=DATE:${end}`);
        }
    } else {
        const start = event.start?.dateTime ? formatDateTimeUtc(event.start.dateTime) : null;
        const end = event.end?.dateTime ? formatDateTimeUtc(event.end.dateTime) : null;
        if (start) {
            lines.push(`DTSTART:${start}`);
        }
        if (end) {
            lines.push(`DTEND:${end}`);
        }
    }

    const summary = property("SUMMARY:", escapeText(event.subject || "(no subject)"));
    if (summary) {
        lines.push(summary);
    }

    const description = eventDescription(event);
    const descLine = description ? property("DESCRIPTION:", escapeText(description)) : null;
    if (descLine) {
        lines.push(descLine);
    }

    const location = event.location?.displayName;
    const locLine = location ? property("LOCATION:", escapeText(location)) : null;
    if (locLine) {
        lines.push(locLine);
    }

    const organizerAddr = event.organizer?.emailAddress?.address;
    if (organizerAddr) {
        const cn = event.organizer?.emailAddress?.name
            ? `;CN=${escapeText(event.organizer.emailAddress.name)}`
            : "";
        lines.push(foldLine(`ORGANIZER${cn}:mailto:${organizerAddr}`));
    }

    for (const attendee of event.attendees ?? []) {
        const addr = attendee.emailAddress?.address;
        if (!addr) {
            continue;
        }
        const cn = attendee.emailAddress?.name ? `;CN=${escapeText(attendee.emailAddress.name)}` : "";
        const role = attendee.type === "required" || !attendee.type ? "REQ-PARTICIPANT" : "OPT-PARTICIPANT";
        lines.push(foldLine(`ATTENDEE;ROLE=${role}${cn}:mailto:${addr}`));
    }

    if (event.isCancelled) {
        lines.push("STATUS:CANCELLED");
    }

    if (!opts.forceStandalone && event.recurrence) {
        const rrule = buildRRule(event.recurrence);
        if (rrule) {
            lines.push(`RRULE:${rrule}`);
        }
    }

    lines.push("END:VEVENT");
    return lines.join(CRLF) + CRLF;
}

/** Full VCALENDAR wrapper around every captured event for one calendar. */
export function renderCalendarIcs(calendarName: string, events: readonly CalendarEvent[]): string {
    const body = events.map((event) => renderEvent(event, { forceStandalone: isStandaloneOnly(event) })).join("");

    return (
        [
            "BEGIN:VCALENDAR",
            "VERSION:2.0",
            "PRODID:-//O365Exporter//EN",
            "CALSCALE:GREGORIAN",
            property("X-WR-CALNAME:", escapeText(calendarName)) ?? ""
        ]
            .filter(Boolean)
            .join(CRLF) +
        CRLF +
        body +
        "END:VCALENDAR" +
        CRLF
    );
}

/**
 * Exceptions and cancelled occurrences are rendered standalone (see
 * export.ts's module doc for why RECURRENCE-ID linking is not attempted).
 */
function isStandaloneOnly(event: CalendarEvent): boolean {
    return event.type === "exception" || Boolean(event.isCancelled);
}
